import {Component, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {StepScreenModel} from '../../../shared/step/step-screen.component';
import {FormErrors} from '../../../../components/forms/validation/form-validator.class';
import {ActivatedRoute} from '@angular/router';
import {WorkObservationsService} from '../../../../domain/model/work/observations/work-observations.service';
import {WorkObservations} from '../../../../domain/model/work/observations/work-observations.model';
import {WorkService} from '../../../../domain/model/work/history/work.service';
import {Entry as GapInfoEntry} from '../../../../domain/model/work/history/gap-info.model';
import {formatMonthYear, separatedBySpace, toOrderValue} from '../../../shared/utils';
import {LocalDateInterval} from '../../../../domain/model/common/values/local-date-interval.class';
import {checkThat} from '../../../../common/typescript/preconditions.util';

//region Other Classes
enum EntryType {
    NORMAL = 1,
    GAP
}

function textFrom(name: string | undefined, duration: LocalDateInterval) {
    const dateText = `${formatMonthYear(duration.from)} - ${formatMonthYear(duration.to)}`;
    return separatedBySpace(name, name ? `(${dateText})` : dateText);
}

abstract class Entry {

    constructor(readonly type: EntryType, readonly text: string, readonly duration: LocalDateInterval) {
        checkThat(!!duration.from && !!duration.to, () => 'Duration must be complete.');
    }

    compareTo(other: Entry): number {
        let result = toOrderValue(this.duration.from) - toOrderValue(other.duration.from);
        if (result === 0) result = toOrderValue(this.duration.to) - toOrderValue(other.duration.to);
        return result;
    }

    contains(other: Entry): boolean {
        return this.duration.from!.isSameOrBefore(other.duration.from) && this.duration.to!.isSameOrAfter(other.duration.to);
    }
}

class NormalEntry extends Entry {

    constructor(entry: GapInfoEntry, name?: string) {
        super(EntryType.NORMAL, textFrom(name ? name : entry.name, entry.duration!), entry.duration!);
    }
}

class GapEntry extends Entry {

    constructor(duration: LocalDateInterval) {
        super(EntryType.GAP, textFrom('Are you missing something here?', duration), duration);
    }
}

//endregion

function durationIsComplete(duration: LocalDateInterval | undefined): boolean {
    return !!duration && !!duration.from && !!duration.to;
}

@Component({
    template: require('./work-observations-screen.component.html'),
    styles: [require('./work-observations-screen.component.scss')]
})
export class WorkObservationsScreenComponent {

    constructor(readonly modelService: WorkObservationsService,
                readonly workService: WorkService,
                readonly route: ActivatedRoute) {

        workService.entriesAndGapInformation().subscribe(gapsInfo => {
            const militaryEntry = gapsInfo.militaryEntry;

            const entries: Array<Entry> = gapsInfo.workEntries.filter(it => durationIsComplete(it.duration)).map(it => new NormalEntry(it))
                .concat(militaryEntry && durationIsComplete(militaryEntry.duration) ? new NormalEntry(militaryEntry, 'Military Service') : [])
                .concat(gapsInfo.gaps.map(it => new GapEntry(it)))
                .sort((a, b) => a.compareTo(b));

            for (let i = 1; i < entries.length;) {
                const previous = entries[i - 1];
                const current = entries[i];

                if (previous.contains(current)) entries.splice(i, 1);
                else ++i;
            }

            this.historyEntries = entries;
        });
    }

    // region Form
    readonly form$ = new ReplaySubject<NgForm>(1);

    @ViewChild(NgForm) set form(value: NgForm) {
        this.form$.next(value);
    }

    // endregion

    stepScreenModel = new StepScreenModel<WorkObservations>({
        initialDataModel: new WorkObservations(),
        specificValidationMessages: {},
        _loadDataFn: () => {
            return this.modelService.getCurrentUserModel();
        },
        _submitDataFn: (model: WorkObservations) => {
            return this.modelService.updateCurrentUserModel(model);
        }
    });

    get model(): WorkObservations {
        return this.stepScreenModel.dataModel;
    }

    get formErrors(): FormErrors {
        return this.stepScreenModel.formErrors;
    }

    entryTypes = EntryType;
    historyEntries: Array<Entry>;
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/work/steps/observations/work-observations-screen.component.ts